import {
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useId,
  SyntheticEvent,
} from 'react';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import classNames from 'classnames';

import { useDebounce } from '../../hooks';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';
import { Option } from '../dropdownOption';
import { Label, Preloader, Badge } from '../common';
import { Icon } from '../../Icon';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';
import { Typography } from '../../Typography';

import { ListBox } from './ListBox';
import { useStyles } from './AutocompleteInput.styles';

const DEBOUNCE_TIMEOUT = 200;

type ValueType = string | undefined;

/**
 * Свойства компонента {@link AutocompleteInput}
 */
export interface AutocompleteInputProps<T extends object = {}> {
  /**
   * Атрибут ID для поля ввода.
   */
  id?: string;

  /**
   * Текст метки.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Флаг, указывающий на режим поиска.
   */
  search?: boolean;

  /**
   * Текущее значение поля ввода.
   */
  value: ValueType;

  /**
   * Обработчик изменения значения ввода.
   */
  onInputValueChange?: (newValue: string) => void;

  /**
   * Обработчик изменения значения.
   */
  onChange: (value: ValueType, item?: Option<T> | null) => void;

  /**
   * Обработчик фокуса.
   */
  onFocus?: () => void;

  /**
   * Обработчик потери фокуса.
   */
  onBlur?: () => void;

  /**
   * Функция для получения метки опции в режиме поиска.
   */
  getSearchOptionLabel?: (option: Option<T>) => string;

  /**
   * Текст подсказки.
   */
  placeholder?: string;

  /**
   * Имя поля ввода.
   */
  name?: string;

  /**
   * Обязательное ли поле ввода.
   */
  required?: boolean;

  /**
   * Разрешено ли очистить поле ввода.
   */
  clearable?: boolean;

  /**
   * Заблокирован ли компонент.
   */
  disabled?: boolean;

  /**
   * Максимальное количество элементов для загрузки.
   */
  limit?: number;

  /**
   * Функция для загрузки элементов.
   */
  loadItems: (filter: Filter) => Promise<Option<T>[]>;

  /**
   * Функция для рендера опции.
   */
  renderOption?: (option: Option<T>) => ReactNode;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode | null;

  /**
   * Наличие ошибки в поле ввода.
   */
  error?: boolean;

  /**
   * Дополнительный текст помощи.
   */
  helperText?: string;

  /**
   * Состояние загрузки.
   */
  loading?: boolean;

  /**
   * Дополнительные фильтры для загрузки элементов.
   */
  additionalFilters?: Record<string, string | number>;

  /**
   * Минимальная длина значения для начала загрузки элементов.
   */
  minInputValueLength?: number;

  /**
   * Идентификатор для тестирования.
   */
  testId?: string;

  /**
   * Атрибут для отмены дефолтного марджина в 16 пикселей и ублока с инпутом.
   */
  withoutMargin?: boolean;
}

/**
 * AutocompleteInput - компонент для отображения поля ввода с автодополнением.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param label - Текст метки.
 * @param labelIcon - Иконка для метки.
 * @param search - Флаг, указывающий на режим поиска.
 * @param value - Текущее значение поля ввода.
 * @param onInputValueChange - Обработчик изменения значения ввода.
 * @param onChange - Обработчик изменения значения.
 * @param onFocus - Обработчик фокуса.
 * @param onBlur - Обработчик потери фокуса.
 * @param getSearchOptionLabel - Функция для получения метки опции в режиме поиска.
 * @param placeholder - Текст подсказки.
 * @param name - Имя поля ввода.
 * @param required - Обязательное ли поле ввода.
 * @param clearable - Разрешено ли очистить поле ввода.
 * @param disabled - Заблокирован ли компонент.
 * @param limit - Максимальное количество элементов для загрузки.
 * @param loadItems - Функция для загрузки элементов.
 * @param renderOption - Функция для рендера опции.
 * @param tooltip - Всплывающая подсказка.
 * @param error - Наличие ошибки в поле ввода.
 * @param helperText - Дополнительный текст помощи.
 * @param loading - Состояние загрузки.
 * @param additionalFilters - Дополнительные фильтры для загрузки элементов.
 * @param minInputValueLength - Минимальная длина значения для начала загрузки элементов.
 * @param testId - Идентификатор для тестирования.
 * @param withoutMargin - Атрибут для отмены дефолтного марджина в 16 пикселей и ублока с инпутом.
 * @returns Возвращает JSX-элемент представляющий компонент AutocompleteInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link AutocompleteInputProps}
 */
export const AutocompleteInput = <T extends object = {}>({
  id,
  label,
  labelIcon,
  search,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  name,
  required = false,
  clearable = false,
  disabled = false,
  limit = 50,
  loadItems,
  renderOption,
  tooltip,
  error = false,
  helperText,
  loading = false,
  additionalFilters,
  minInputValueLength = 1,
  testId,
  withoutMargin = false,
  onInputValueChange,
  getSearchOptionLabel,
}: AutocompleteInputProps<T>) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<Option<T>>(loadItems);

  const [isLoading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<Option<T> | null>(null);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);

  const isItemsLoading = isLoading || status === 'loading';
  const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT);
  const isThereSomeGroup = items.some((option) => option.group);

  const autocompleteOpenHandler = () => {
    setOpen(true);
  };

  const autocompleteCloseHandler = () => {
    setOpen(false);
  };

  const autocompleteFocusHandler = () => {
    setInputValue('');
    onFocus?.();
  };

  const autocompleteGetInputValue = () => {
    if (!loading && !open && !inputValue) {
      return (selectedItem?.label || '').toString().split('\n').join(', ');
    }

    return inputValue;
  };

  const autocompleteChangeHandler = (
    _: SyntheticEvent,
    nextValue: Option<T> | null,
  ) => {
    onChange(nextValue?.value || '', nextValue);
    setSelectedItem(nextValue);
    resetItems();
  };

  const autocompleteInputChangeHandler = (
    _: SyntheticEvent,
    nextValue: string,
    reason: string,
  ) => {
    if (reason === 'reset') {
      setInputValue('');
      setFilter({ ...filter, offset: 0, query: '' });
      return;
    }

    if (reason === 'clear') {
      setSelectedItem(null);
    }

    if (reason === 'input') {
      setLoading(true);
      setFilter({ ...filter, offset: 0, query: nextValue });
    }

    if (onInputValueChange) {
      onInputValueChange(nextValue);
    }

    setInputValue(nextValue);
  };

  const autocompleteGetOptionSelected = (option: Option) =>
    option.value === value;

  const autocompleteGetOptionLabel = (option: Option) => option.label;

  const requestNextItems = () => {
    requestItems?.({ ...filter, ...additionalFilters }, 'get');
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
  };

  useEffect(() => {
    if (
      !open ||
      (debouncedInputValue !== '' &&
        debouncedInputValue.length < minInputValueLength)
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        ...additionalFilters,
        offset: 0,
        query: debouncedInputValue,
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  useEffect(() => {
    if (!open) {
      setInputValue('');
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!value) {
        setSelectedItem(null);
        return;
      }

      if (!inputValue && value !== selectedItem?.value) {
        const itemsById = await loadItems?.({
          limit: 50,
          offset: 0,
          query: '',
          id: value,
          ...additionalFilters,
        });

        const itemById = itemsById?.find((item) => item.value === value);

        setSelectedItem(itemById || null);
      }
    })();

    // eslint-disable-next-line
  }, [value]);

  const customListboxProps = {
    dataLength: items.length,
    next: requestNextItems,
    hasMore,
    loading: isItemsLoading,
    long: !!renderOption,
  };
  return (
    <Box
      className={classNames(
        (!withoutMargin || !search) && classes.autocompleteInput,
        'aqa_autocomplete_input',
        classes.autocompleteInputContainer,
      )}
    >
      {!search && label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
          className="aqa_autocomplete_input_label"
        />
      )}

      <Autocomplete<Option<T>, false, boolean>
        blurOnSelect
        data-testid={testId}
        classes={{
          paper: classes.list,
          option: classNames(classes.option, {
            [classes.defaultOption]: !renderOption,
          }),
        }}
        open={open}
        filterOptions={(options) => options}
        onOpen={autocompleteOpenHandler}
        value={selectedItem}
        disableClearable={!clearable || !selectedItem}
        onClose={autocompleteCloseHandler}
        onChange={autocompleteChangeHandler}
        onFocus={autocompleteFocusHandler}
        onBlur={onBlur}
        disabled={disabled || loading}
        inputValue={autocompleteGetInputValue()}
        onInputChange={autocompleteInputChangeHandler}
        slotProps={{
          listbox: {
            component: ListBox,
            ...customListboxProps,
          },
        }}
        renderOption={
          renderOption &&
          ((props, option) => (
            <li {...props} key={option.value}>
              {renderOption(option)}
            </li>
          ))
        }
        {...(isThereSomeGroup
          ? {
              groupBy: ({ group }) => {
                if (group) {
                  return group;
                }

                return 'Заявки без группировки по коллекциям';
              },
            }
          : {})}
        renderGroup={(params) => (
          <>
            <Box px="16" pt="8" pb="4">
              <Typography
                component="span"
                variant="caption"
                color="text.light.tertirary"
              >
                {params.group}
              </Typography>
            </Box>
            <Box>{params.children}</Box>
          </>
        )}
        isOptionEqualToValue={autocompleteGetOptionSelected}
        getOptionLabel={
          search ? getSearchOptionLabel : autocompleteGetOptionLabel
        }
        options={!filter.offset && isItemsLoading ? [] : items}
        loading={isItemsLoading}
        loadingText={
          <Box display="flex" justifyContent="center" p="16">
            <CircularProgress size={30} />
          </Box>
        }
        noOptionsText="Ничего не найдено..."
        popupIcon={
          search ? null : (
            <Box m="4">
              <Icon
                name="arrow_triangle_down"
                size="16"
                color="text.dark.secondary"
              />
            </Box>
          )
        }
        clearIcon={
          search ? (
            <Icon
              name="cross_large_outline"
              size="20"
              color="original.brand.primary"
            />
          ) : (
            <Icon name="dismiss_substract" color="stroke.secondary" size="24" />
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            id={inputId}
            name={name}
            classes={{
              root: `${classes.input} ${
                search ? classes.search : ''
              } aqa_select`,
            }}
            variant="outlined"
            placeholder={loading ? 'Загрузка...' : placeholder || label}
            error={error}
            helperText={helperText}
            {...(loading
              ? {
                  slotProps: {
                    input: {
                      endAdornment: <Preloader />,
                    },
                  },
                }
              : {
                  slotProps: {
                    input: {
                      ...params.InputProps,
                      startAdornment: search ? (
                        <Icon
                          name="search_outline"
                          size="20"
                          color="text.light.tertirary"
                        />
                      ) : (
                        selectedItem?.badge &&
                        !open &&
                        !inputValue && (
                          <Box pl="16" mr="-8">
                            <Badge
                              color={selectedItem.badge?.color}
                              title={selectedItem.badge?.title}
                              description={selectedItem.badge?.description}
                            />
                          </Box>
                        )
                      ),
                    },
                  },
                })}
          />
        )}
      />
    </Box>
  );
};
